import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

/** Submit a cash top-up request (balance updates on approval). */
export default function AddBalanceButton({ onSubmitted, className = "", label = "Add Balance" }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const minor = Math.round(parseFloat(amount) * 100);
    if (!minor || Number.isNaN(minor) || minor <= 0) {
      toast.error("Enter a valid positive amount.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/billing/payment-requests", { amount_minor: minor, note: note || null });
      toast.success("Balance request submitted — awaiting approval.");
      setOpen(false);
      setAmount("");
      setNote("");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        data-testid="add-balance-btn"
        className={`rounded-lg ${className}`}
        onClick={() => setOpen(true)}
      >
        <Plus className="w-4 h-4 mr-1.5" weight="bold" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent data-testid="add-balance-dialog">
          <DialogHeader>
            <DialogTitle>Add Balance</DialogTitle>
            <DialogDescription>
              Submit a cash top-up request. Your balance updates only after it is approved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Amount (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  data-testid="add-balance-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-7"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="50.00"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea
                data-testid="add-balance-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reference / payment method details"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="submit-balance-request-btn"
              onClick={submit}
              disabled={saving}
              className="rounded-lg"
            >
              {saving ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
